import React, { useState } from 'react';
import {
  Container,
  Tabs,
  Tab,
  Content,
  FieldsGrid,
  Field,
  Label,
  Value,
  BooleanValue,
  List,
  ListItem,
  EmptyValue,
} from './styled/InstanceDetails.styled';

const instanceDetails = ({ instanceInfo }) => {
  const [tab, setTab] = useState('plan');

  if (!instanceInfo) {
    return null;
  }

  const renderBoolean = (value) => (
    <BooleanValue $enabled={Boolean(value)}>
      {value ? '✓' : '✕'}
    </BooleanValue>
  );

  const renderValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return <EmptyValue>—</EmptyValue>;
    }

    return <Value>{value}</Value>;
  };

  const renderList = (items) => {
    if (!Array.isArray(items) || items.length === 0) {
      return <EmptyValue>—</EmptyValue>;
    }

    return (
      <List>
        {items.map((item, index) => (
          <ListItem key={`${item}-${index}`}>{item}</ListItem>
        ))}
      </List>
    );
  };

  const renderVolumeData = (volumeData) => {
    if (volumeData === null || volumeData === undefined) {
      return <EmptyValue>—</EmptyValue>;
    }

    const volumeInGB = volumeData / 1024;

    return (
      <Value>
        {volumeInGB % 1 === 0
          ? volumeInGB
          : volumeInGB.toFixed(2)}{' '}
        GB (
        {volumeData}
        MB)
      </Value>
    );
  };

  const renderInfinity = (value) => {
    if (value === 10000) {
      return <Value>∞</Value>;
    }

    return renderValue(value);
  };

  const formatDate = (date) => {
    if (!date) {
      return '—';
    }

    const [year, month, day] = date.split('T')[0].split('-');

    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(Number(value));
  };

  const formatPhone = (value) => {
    if (!value) {
      return '—';
    }
    let numbers = String(value).replace(/\D/g, '');

    // Remove o código do Brasil, caso esteja presente
    if (numbers.startsWith('55')) {
      numbers = numbers.substring(2);
    }

    // (DD) NNNNN-NNNN
    if (numbers.length === 11) {
      return numbers.replace(
        /^(\d{2})(\d{5})(\d{4})$/,
        '($1) $2-$3',
      );
    }

    // (DD) NNNN-NNNN
    if (numbers.length === 10) {
      return numbers.replace(
        /^(\d{2})(\d{4})(\d{4})$/,
        '+55 ($1) $2-$3',
      );
    }

    return value;
  };

  const renderPlanTab = () => (
    <FieldsGrid>
      <Field>
        <Label>Nome</Label>
        {renderValue(instanceInfo?.name)}
      </Field>

      <Field>
        <Label>Plano</Label>
        {renderValue(instanceInfo?.plan)}
      </Field>

      <Field>
        <Label>Memória</Label>
        {renderVolumeData(instanceInfo?.volumeData)}
      </Field>

      <Field>
        <Label>Usuários proprietários</Label>
        {renderValue(instanceInfo?.superS)}
      </Field>

      <Field>
        <Label>Usuários cientistas</Label>
        {renderValue(instanceInfo?.scientists)}
      </Field>

      <Field>
        <Label>Usuários convidados</Label>
        {renderValue(instanceInfo?.guests)}
      </Field>

      <Field>
        <Label>Limite de stories por dashboard</Label>
        {renderValue(instanceInfo?.snapshots)}
      </Field>

      <Field>
        <Label>Agendamento de stories</Label>
        {renderBoolean(instanceInfo?.scheduledSnapshotAllowed)}
      </Field>

      <Field>
        <Label>Limite de históricos de base</Label>
        {renderValue(instanceInfo?.maxHistory)}
      </Field>

      <Field>
        <Label>Agendamento de histórico</Label>
        {renderBoolean(instanceInfo?.scheduledHistoryAllowed)}
      </Field>

      <Field>
        <Label>Possui link público</Label>
        {renderBoolean(instanceInfo?.publicLink)}
      </Field>

      <Field>
        <Label>Expiração do link (em dias)</Label>
        {renderInfinity(instanceInfo?.publicLinkExpirationDays)}
      </Field>

      <Field>
        <Label>Senha para link</Label>
        {renderBoolean(instanceInfo?.publicLinkPwd)}
      </Field>

      <Field>
        <Label>Gráficos avançados</Label>
        {renderBoolean(instanceInfo?.advancedCharts)}
      </Field>

      <Field>
        <Label>KPIs externos</Label>
        {renderBoolean(instanceInfo?.externalKpis)}
      </Field>

      <Field>
        <Label>Limite de subtabelas por dashboard</Label>
        {renderInfinity(instanceInfo?.subsetTableLimit)}
      </Field>

      <Field>
        <Label>Atualizações de relatórios por hora</Label>
        {renderValue(instanceInfo?.attemptsPerHour)}
      </Field>

      {process.env.REACT_APP_FIREBASE_PROJECT_LABEL !== 'benner-metrics' && (
        <Field>
          <Label>Permitir criação de relatório Legal One</Label>
          {renderBoolean(instanceInfo?.l1reportActive)}
        </Field>
      )}

      <Field>
        <Label>Assistente Virtual</Label>
        {renderBoolean(instanceInfo?.gptApi)}
      </Field>

      <Field $fullWidth>
        <Label>Agentes</Label>
        {renderList(instanceInfo?.gptAgents)}
      </Field>

      <Field>
        <Label>Franquia de tokens por mês</Label>
        {renderValue(instanceInfo?.gptTokens)}
      </Field>
    </FieldsGrid>
  );

  const renderOperationTab = () => (
    <FieldsGrid>
      <Field>
        <Label>Situação operacional</Label>
        {renderValue(instanceInfo?.operationalStatus)}
      </Field>

      <Field>
        <Label>Identificador Externo</Label>
        {renderValue(instanceInfo?.external_identifier)}
      </Field>

      <Field>
        <Label>Identificador Financeira</Label>
        {renderList(instanceInfo?.financialIdentifier)}
      </Field>

      <Field>
        <Label>Identificador Jira</Label>
        {renderList(instanceInfo?.jiraIdentifier)}
      </Field>

      <Field>
        <Label>Provedor de Identidade (SSO)</Label>
        {renderValue(instanceInfo?.loginProvider)}
      </Field>

      <Field>
        <Label>Domínios autorizados</Label>
        {renderList(instanceInfo?.loginProviderDomains)}
      </Field>

      <Field>
        <Label>Data da assinatura</Label>
        {renderValue(formatDate(instanceInfo?.signatureDate))}
      </Field>

      <Field>
        <Label>Início da operação</Label>
        {renderValue(formatDate(instanceInfo?.operationalDate))}
      </Field>

      <Field>
        <Label>Mensalidade</Label>
        {renderValue(formatCurrency(instanceInfo?.projectValue))}
      </Field>

      <Field>
        <Label>Implantação</Label>
        {renderValue(formatCurrency(instanceInfo?.installationPrice))}
      </Field>

      <Field>
        <Label>Integração connect</Label>
        {renderValue(instanceInfo?.connectIntegration)}
      </Field>

      <Field>
        <Label>Quantidade de relatórios</Label>
        {renderValue(instanceInfo?.reportAmount)}
      </Field>
    </FieldsGrid>
  );

  const renderContactTab = () => (
    <FieldsGrid>
      <Field>
        <Label>Nome do contato</Label>
        {renderValue(instanceInfo?.contactName)}
      </Field>

      <Field>
        <Label>Telefone para contato</Label>
        {renderValue(formatPhone(instanceInfo?.contactPhone))}
      </Field>

      <Field>
        <Label>Email para contato</Label>
        {renderValue(instanceInfo?.contactEmail)}
      </Field>

      <Field $fullWidth>
        <Label>Observações</Label>
        {renderValue(instanceInfo?.obs)}
      </Field>
    </FieldsGrid>
  );

  const renderAntecipeiTab = () => (
    <FieldsGrid>
      <Field>
        <Label>Possui Antecipei</Label>
        {renderBoolean(instanceInfo?.push)}
      </Field>

      <Field>
        <Label>Mensalidade</Label>
        {renderValue(formatCurrency(instanceInfo?.antecipeiValue))}
      </Field>

      <Field>
        <Label>Número de CNPJs monitorados</Label>
        {renderValue(instanceInfo?.monitoring)}
      </Field>

      <Field>
        <Label>Franquia de processos</Label>
        {renderValue(instanceInfo?.limitLegalSuits)}
      </Field>

      <Field>
        <Label>Frequência de busca</Label>
        {renderValue(instanceInfo?.frequency)}
      </Field>

      <Field>
        <Label>Tribunais</Label>
        {renderValue(instanceInfo?.courts)}
      </Field>

      <Field>
        <Label>Dia do monitoramento</Label>
        {renderValue(instanceInfo?.weekDayAntecipei)}
      </Field>

      <Field>
        <Label>Valor por processo excedente</Label>
        {renderValue(formatCurrency(instanceInfo?.newProcess))}
      </Field>
    </FieldsGrid>
  );

  const renderTabContent = () => {
    switch (tab) {
      case 'plan':
        return renderPlanTab();

      case 'operation':
        return renderOperationTab();

      case 'contact':
        return renderContactTab();

      case 'antecipei':
        return renderAntecipeiTab();

      default:
        return null;
    }
  };

  return (
    <Container>
      <Tabs>
        <Tab
          $active={tab === 'plan'}
          onClick={() => setTab('plan')}
        >
          Plano
        </Tab>

        <Tab
          $active={tab === 'operation'}
          onClick={() => setTab('operation')}
        >
          Operacional
        </Tab>

        <Tab
          $active={tab === 'contact'}
          onClick={() => setTab('contact')}
        >
          Contato
        </Tab>

        <Tab
          $active={tab === 'antecipei'}
          onClick={() => setTab('antecipei')}
        >
          Antecipei
        </Tab>
      </Tabs>

      <Content>
        {renderTabContent()}
      </Content>
    </Container>
  );
};

export default instanceDetails;
